import { useIsFocused } from '@react-navigation/native';
import {
  alreadyRedirectedOnAppStartAtom,
  currentTutorialAtom,
  isInsideOnboardingAtom,
  isLoggedInAtom,
  isOnboardingAtom,
  isStartingAtom,
  pauseModalsAtom,
} from 'app/atoms';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Text } from 'app/design/primitives/text';
import { useIsBlockingModalVisible, isBlockingModalVisible } from 'app/hooks/use-is-blocking-modal-visible';
import {
  FEATURE_PRESENTATIONS,
  readPresentationAccounts,
  selectFeaturePresentation,
  type FeaturePresentation as Presentation,
} from 'app/lib/feature-presentations';
import { storage, type StorageKey } from 'app/lib/storage';
import { useOnePreference } from 'app/network/preferences';
import { useUser } from 'app/network/user';
import {
  FeaturePresentation,
  GuardianAnnouncementIllustration,
} from 'app/shopify/components/feedback/FeaturePresentation';
import { Tutorial } from 'app/types/tutorial';
import { useTheme } from 'app/shopify/theme';
import { useAtomValue } from 'jotai';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const illustrations: Record<string, React.ReactNode> = {
  guardian: <GuardianAnnouncementIllustration />,
};

// A JS runtime is an app run. Preserve this across home remounts, resizing, and logout.
let presentationReservedThisRun = false;

function usePresentationReadiness(startupReady: boolean) {
  const focused = useIsFocused();
  const loggedIn = useAtomValue(isLoggedInAtom);
  const starting = useAtomValue(isStartingAtom);
  const onboarding = useAtomValue(isOnboardingAtom);
  const insideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const redirected = useAtomValue(alreadyRedirectedOnAppStartAtom);
  const paused = useAtomValue(pauseModalsAtom);
  const tutorial = useAtomValue(currentTutorialAtom);
  const blocked = useIsBlockingModalVisible();
  const { data: user, isFetching: refreshingUser } = useUser();
  const tutorials = useOnePreference('tutorialsCompleted');
  const blockedByStartup = [starting, onboarding, insideOnboarding, redirected, paused, tutorial, blocked].some(
    Boolean,
  );
  const ready = [
    startupReady,
    focused,
    loggedIn,
    !blockedByStartup,
    !refreshingUser,
    user?.status === 'complete',
    Boolean(user?.acceptedGuidelineDate),
    tutorials.isSuccess,
    tutorials.data?.value?.includes(Tutorial.HomeExplore),
  ].every(Boolean);
  return { ready, user };
}

export function FeaturePresentationPrompt({ startupReady }: { startupReady: boolean }) {
  const theme = useTheme();
  const { t } = useTranslation('featurePresentations');
  const { ready: startupSettled, user } = usePresentationReadiness(startupReady);
  const [history, setHistory] = useState<Partial<Record<StorageKey, string[]>>>();
  const [selected, setSelected] = useState<{ presentation: Presentation; userId: string }>();
  const markedShown = useRef(false);

  useEffect(() => {
    let active = true;
    void Promise.all(
      FEATURE_PRESENTATIONS.map(
        async ({ storageKey }) => [storageKey, readPresentationAccounts(await storage.readString(storageKey))] as const,
      ),
    ).then((entries) => {
      if (active) setHistory(Object.fromEntries(entries));
    });
    return () => {
      active = false;
    };
  }, []);

  const ready = Boolean(startupSettled && history);

  const present = useEffectEvent(() => {
    if (!ready || !user || !history || presentationReservedThisRun || isBlockingModalVisible()) return;
    const presentation = selectFeaturePresentation({
      presentations: FEATURE_PRESENTATIONS,
      userId: user.id,
      accountCreatedAt: user.createdAt,
      enabledFlags: user.featureFlags?.enabled ?? [],
      flagReleaseDates: user.featureFlags?.releasedAt,
      seenByKey: history,
    });
    if (!presentation) return;
    setSelected({ presentation, userId: user.id });
  });

  useEffect(() => {
    if (!ready) return;
    // Give pending startup effects and the home tutorial time to claim priority.
    const timer = setTimeout(present, 600);
    return () => clearTimeout(timer);
  }, [ready]);

  const markSeen = () => {
    if (!selected || markedShown.current) return;
    presentationReservedThisRun = true;
    markedShown.current = true;
    const { storageKey } = selected.presentation;
    void storage
      .readString(storageKey)
      .then((value) =>
        storage.saveString(
          storageKey,
          JSON.stringify([...new Set([...readPresentationAccounts(value), selected.userId])]),
        ),
      );
  };
  const selectionStillEligible =
    selected &&
    user &&
    selectFeaturePresentation({
      presentations: [selected.presentation],
      userId: user.id,
      accountCreatedAt: user.createdAt,
      enabledFlags: user.featureFlags?.enabled ?? [],
      flagReleaseDates: user.featureFlags?.releasedAt,
      seenByKey: { [selected.presentation.storageKey]: [] },
    });
  const open = Boolean(selected && ready && selected.userId === user?.id && selectionStillEligible);
  return (
    <BottomSheet
      open={open}
      onOpen={markSeen}
      onClose={() => setSelected(undefined)}
      showIndicator={false}
      indicatorStyle={{ height: 0, marginTop: 0, marginBottom: 8 }}
      containerStyle={{
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        backgroundColor: theme.colors['surface.default'],
      }}
      contentStyle={{ maxHeight: '100%' }}
    >
      {selected ? (
        <FeaturePresentation
          announcementLabel={t('title')}
          closeLabel={t('close')}
          title={t(`${selected.presentation.id}.title`)}
          description={
            <Trans
              ns="featurePresentations"
              i18nKey={`${selected.presentation.id}.description`}
              components={{ strong: <Text fontWeight="700" /> }}
            />
          }
          illustration={illustrations[selected.presentation.id]}
          onClose={() => setSelected(undefined)}
        />
      ) : null}
    </BottomSheet>
  );
}
